import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { studioApi, idempotencyKey, requestKey, errorKey, STUDIO_CONTRACT_VERSION } from '../data/studioApi';
import { readEntry, saveEntry, clearEntry, emptyEntry, readEditorDraft, saveEditorDraft, clearEditorDraft } from '../data/entry';
import { exportReel } from '../data/exportReel';
import { uploadAsset } from '../data/uploads';
import { ART_DEFAULT_RATIO, STUDIO_FORMATS, formatAvailable, isArtProject } from '../enums/formats';
import { STUDIO_HOME } from '../enums/routes';
import useOnboarding from './useOnboarding';
import usePayments from './usePayments';
import useAccounts from './useAccounts';
import useMyPersonality from './useMyPersonality';
import useAutomations from './useAutomations';
import useArt from './useArt';
import useAttachments from './useAttachments';
import useStableActions from './useStableActions';
import useStudioTracking from './useStudioTracking';
import { createDraft } from './useDraft';
import deepEqual from '../utils/equal';

const ACTIVE = ['queued', 'running', 'waiting_user'];
const NO_FEATURES = {};
const NONE = [];
const POLLING = ['queued', 'running'];
const LIST = { page: 1, per_page: 50 };
// The trending gallery shows the whole ranked page; the composer only its first topics.
const TOPICS = { page: 1, per_page: 50 };
const MAX_POLL_FAILURES = 5;
// A run on screen that the server moved on from: its question was answered elsewhere, expired
// (an image question is skipped by core after `question_ttl_hours`) or its version is behind.
const STALE_RUN = ['action_not_open', 'action_version_conflict', 'version_conflict', 'invalid_run_transition'];
// A request that may have reached core without its answer reaching the page.
const transient = failure => failure?.code === 'network' || failure?.status >= 500;
// A tab that comes back after this long reads its open image question again.
const RESUME_READ_MS = 60e3;
const NO_ONBOARDING = ['onboarding_not_started', 'onboarding_unavailable'];
// `scope` is the profile whose workspace `projects`, `memories` and `topics` belong to (contract
// 2.0.0); `scopeReady` once they are loaded for it.
const emptyAccount = status => ({ status, capabilities: null, creator: null, onboarding: null, usage: null, scope: null, scopeReady: false, projects: [], topics: [], memories: [] });
// An art project (contract 2.7.0 proposal) keeps its art revisions apart from script revisions.
const emptyWorkspace = (id = null, status = 'idle') => ({ id, status, project: null, messages: [], revisions: [], artRevisions: [], run: null });
const absent = error => error?.code === 'creator_not_initialized';
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const upsert = (list, item) => list.some(entry => entry.id === item.id) ? list.map(entry => entry.id === item.id ? item : entry) : [item, ...list];

// Messages are ordered oldest first; a chat rarely spans more than one page.
async function allMessages(chatId) {
  const first = await studioApi.messages(chatId, { page: 1, per_page: 100 });
  const rest = [];
  for (let page = 2; page <= Math.min(first.last_page, 10); page++) rest.push(...(await studioApi.messages(chatId, { page, per_page: 100 })).data);
  return [...first.data, ...rest];
}

export default function useStudio() {
  const { t, i18n } = useTranslation('studio');
  const locale = { pt: 'pt_BR', en: 'en_US', es: 'es_ES' }[i18n.language?.slice(0, 2)] || 'pt_BR';
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const owner = auth.signed ? auth.user?.id : null;
  const [account, setAccount] = useState(() => emptyAccount(owner ? 'loading' : 'anonymous'));
  const [workspace, setWorkspace] = useState(() => emptyWorkspace());
  const [entry, setEntry] = useState(readEntry);
  const [topic, setTopic] = useState(() => readEntry().topic || null);
  const [dismissed, setDismissed] = useState([]);
  const [modal, setModal] = useState(null);
  const modalRef = useRef(modal);
  modalRef.current = modal;
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // The reply being typed lives outside React state (useDraft): a keystroke renders the
  // field alone, never the Studio.
  const [replyDraft] = useState(() => createDraft(''));
  const setReply = replyDraft.set;
  const [replyVoice, setReplyVoice] = useState(null);
  const [editor, setEditor] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [topicsState, setTopicsState] = useState('ready');
  const [briefTopics, setBriefTopics] = useState({});
  const [hasPrompted, setHasPrompted] = useState(false);
  const creationKey = useRef(idempotencyKey('project'));
  const submitLock = useRef(false);
  const savingRef = useRef(null);
  const editorRef = useRef(null);
  const editorOwner = useRef(null);
  const accountGeneration = useRef(0);
  const scopeGeneration = useRef(0);
  const workspaceGeneration = useRef(0);
  const mounted = useRef(true);
  const briefTopicRequests = useRef(new Set());
  const outOfCredits = useRef(null);
  // Selects a profile by id: the one an onboarding connected, or the one an opened project belongs to.
  const profileAdded = useRef(null);

  const projectId = location.pathname.match(/^\/studio\/projects\/([^/]+)/)?.[1];
  const capabilities = account.capabilities;
  const features = useMemo(() => capabilities?.features || NO_FEATURES, [capabilities]);
  const pollMs = (capabilities?.limits?.poll_interval_seconds || 3) * 1000;
  // Every Instagram profile is a separate workspace (contract 2.0.0); the active one scopes
  // the projects, memories, topics and prompt audio, and generation uses its voice.
  const accounts = useAccounts({ owner, creator: account.creator, features, limits: capabilities?.limits });
  const profile = accounts.active;
  const profileId = profile?.id || null;
  const memoryOn = !!profile?.memory_enabled && !!features.memory;
  const scopeRef = useRef(profileId);
  scopeRef.current = profileId;
  profileAdded.current = accounts.select;
  const current = workspace.id === projectId && workspace.status === 'ready' ? workspace.project : undefined;
  const run = current ? workspace.run : undefined;
  const revision = workspace.revisions.find(r => r.id === current?.latest_revision_id);
  const active = run && ACTIVE.includes(run.status);
  const formatLocked = !!current || hasPrompted || (entry.resume === true && entry.formatSelected === true);
  // Running out of credits also offers the way out: the next plan or credit packs.
  const fail = useCallback(failure => {
    const key = errorKey(failure);
    setError(key);
    if (key === 'credits') outOfCredits.current?.();
    return false;
  }, []);
  const act = useCallback(async fn => {
    try { setError(null); const value = await fn(); return value ?? true; }
    catch (failure) { return fail(failure); }
  }, [fail]);
  const keepEditor = useCallback(next => { editorRef.current = next; setEditor(next); }, []);

  useEffect(() => () => { mounted.current = false; }, []);

  // ── account: capabilities → creator (or its onboarding) → usage; the workspace data follows the profile ──
  const loadAccount = useCallback(async () => {
    const generation = ++accountGeneration.current;
    const alive = () => mounted.current && accountGeneration.current === generation;
    if (!owner) { setAccount(emptyAccount('anonymous')); return; }
    setAccount(previous => ({ ...previous, status: previous.status === 'ready' ? 'ready' : 'loading' }));
    try {
      const caps = await studioApi.capabilities();
      if (caps.contract_version !== STUDIO_CONTRACT_VERSION) console.warn(`Studio contract ${caps.contract_version} differs from the web client (${STUDIO_CONTRACT_VERSION}).`);
      if (!caps.available) { if (alive()) setAccount({ ...emptyAccount('unavailable'), capabilities: caps }); return; }
      const creator = await studioApi.creator().catch(failure => { if (absent(failure)) return null; throw failure; });
      // A creator made from the @ is pending until its onboarding completes; resume it.
      const onboarding = creator?.onboarding_status === 'pending'
        ? await studioApi.onboarding().catch(failure => { if (NO_ONBOARDING.includes(failure.code)) return null; throw failure; })
        : null;
      const usage = creator ? await studioApi.usage() : null;
      // The profile's projects, memories and topics stay: they reload when the profile changes.
      if (alive()) setAccount(previous => ({ ...previous, status: 'ready', capabilities: caps, creator, onboarding, usage }));
    } catch (failure) {
      if (!alive()) return;
      setAccount(previous => ({ ...previous, status: failure.code === 'studio_unavailable' ? 'unavailable' : 'error' }));
      fail(failure);
    }
  }, [owner, fail]);
  useEffect(() => { setWorkspace(emptyWorkspace()); setDismissed([]); setAccount(emptyAccount(owner ? 'loading' : 'anonymous')); loadAccount(); }, [loadAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the active profile's workspace: projects, memories (when its memory is on), topics ──
  // A response for a profile that is no longer active is dropped.
  const inScope = id => mounted.current && scopeRef.current === id;
  const loadScope = useCallback(async id => {
    const generation = ++scopeGeneration.current;
    const alive = () => mounted.current && scopeGeneration.current === generation && scopeRef.current === id;
    setAccount(previous => previous.scope === id ? previous : { ...previous, scope: id, scopeReady: false, projects: [], memories: [], topics: [] });
    if (!id) { setAccount(previous => ({ ...previous, scopeReady: true })); return; }
    try {
      const [projects, memories, topics] = await Promise.all([
        studioApi.projects({ ...LIST, status: 'active', profile_id: id }).then(page => page.data),
        memoryOn ? studioApi.memories({ ...LIST, profile_id: id }).then(page => page.data) : [],
        // Trending topics are an upper-plan feature; a failed read only hides the suggestions.
        features.trending_topics ? studioApi.topics({ ...TOPICS, profile_id: id }).then(page => page.data).catch(() => []) : [],
      ]);
      if (alive()) setAccount(previous => ({ ...previous, scope: id, scopeReady: true, projects, memories, topics }));
    } catch (failure) { if (alive()) fail(failure); }
  }, [memoryOn, features.trending_topics, fail]);
  useEffect(() => {
    if (account.status === 'ready') loadScope(profileId);
  }, [account.status, profileId, loadScope]);

  const refreshUsage = useCallback(() => studioApi.usage().then(usage => mounted.current && setAccount(a => ({ ...a, usage }))).catch(() => null), []);
  const refreshProjects = useCallback(() => {
    const id = scopeRef.current;
    if (!id) return Promise.resolve();
    return studioApi.projects({ ...LIST, status: 'active', profile_id: id }).then(page => inScope(id) && setAccount(a => ({ ...a, projects: page.data }))).catch(() => null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshMemories = useCallback(() => {
    const id = scopeRef.current;
    if (!id || !memoryOn) return Promise.resolve();
    return studioApi.memories({ ...LIST, profile_id: id }).then(page => inScope(id) && setAccount(a => ({ ...a, memories: page.data }))).catch(() => null);
  }, [memoryOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── workspace: project (= its chat), messages, revisions and the active run ──
  const loadProject = useCallback(async (id, { quiet = false } = {}) => {
    const generation = ++workspaceGeneration.current;
    const alive = () => mounted.current && workspaceGeneration.current === generation;
    if (!quiet) setWorkspace(emptyWorkspace(id, 'loading'));
    try {
      const project = await studioApi.project(id);
      // A link to another profile's project opens that profile's workspace.
      if (project.profile_id && project.profile_id !== scopeRef.current) profileAdded.current?.(project.profile_id);
      const art = isArtProject(project);
      const [messages, revisions, activeRun] = await Promise.all([
        allMessages(project.chat_id),
        (art ? studioApi.artRevisions(project.id, LIST) : studioApi.revisions(project.id, LIST)).then(page => page.data.slice().reverse()),
        project.active_run_id ? studioApi.run(project.active_run_id) : null,
      ]);
      if (!alive()) return null;
      // A finished run is detached from the project; keep showing it for this session.
      setWorkspace(previous => ({ id, status: project.status === 'archived' ? 'missing' : 'ready', project, messages,
        revisions: art ? [] : revisions, artRevisions: art ? revisions : [],
        run: activeRun || (previous.id === id ? previous.run : null) }));
      return project;
    } catch (failure) {
      if (!alive()) return null;
      if (!quiet) setWorkspace(emptyWorkspace(id, 'missing'));
      if (failure.status !== 404) fail(failure);
      return null;
    }
  }, [fail]);
  useEffect(() => {
    if (!projectId || !owner || account.status !== 'ready' || !account.creator) return;
    if (workspace.id === projectId && workspace.status !== 'idle') return;
    loadProject(projectId);
  }, [projectId, owner, account.status, account.creator, workspace.id, workspace.status, loadProject]);

  // An art project's working copy, autosave, brand kit, galleries and chat-edit scope.
  const art = useArt({ owner, current, revisions: current ? workspace.artRevisions : NONE, active, profileId, features, setWorkspace, loadProject, setError, mounted });
  // The images attached to the prompt being written (2.7.0 proposal), where an art takes
  // them: the home with an art format, an art project's chat. They upload as the creator's
  // `art_image` assets; a home draft that leaves the art formats drops its images.
  const homeFormat = STUDIO_FORMATS.find(item => item.id === entry.format);
  const attachable = !!auth.signed && !!profileId && (current ? isArtProject(current) : !!homeFormat?.art && formatAvailable(homeFormat, capabilities));
  const attachments = useAttachments({ scope: current ? current.id : 'home', enabled: attachable, upload: art.images?.upload });
  const { clear: clearAttachments, reset: resetAttachments } = attachments;
  useEffect(() => { if (!homeFormat?.art) clearAttachments('home'); }, [homeFormat?.art, clearAttachments]);

  // The project's sourced topic, whose evidence its generations cite: the live list
  // has it while it is offered, an older one is read by id (null when unreadable).
  const briefTopicId = current?.brief?.topic?.kind === 'sourced' ? current.brief.topic.topic_id : null;
  const liveBriefTopic = briefTopicId ? account.topics.find(item => item.id === briefTopicId) : null;
  useEffect(() => {
    if (!briefTopicId || !profileId || liveBriefTopic || !features.trending_topics || briefTopicRequests.current.has(briefTopicId)) return;
    briefTopicRequests.current.add(briefTopicId);
    studioApi.topic(briefTopicId, { profile_id: profileId }).catch(() => null)
      .then(found => mounted.current && setBriefTopics(topics => ({ ...topics, [briefTopicId]: found })));
  }, [briefTopicId, liveBriefTopic, features.trending_topics, profileId]);
  const projectTopic = liveBriefTopic || (briefTopicId && briefTopics[briefTopicId]) || null;

  // ── run polling (JSON replay; SSE is off by default on the backend) ──
  const settleRun = useCallback(next => {
    refreshUsage();
    if (next.status === 'succeeded') { loadProject(next.project_id, { quiet: true }); refreshProjects(); refreshMemories(); }
    else loadProject(next.project_id, { quiet: true });
  }, [refreshUsage, loadProject, refreshProjects, refreshMemories]);
  // The run read again by its id, never through its project (which keeps a finished run it
  // no longer lists as the one on screen): a copy at least as new replaces it, a queued or
  // running one is polled again, and one that ended is settled as the polling would.
  const resyncRun = useCallback(async id => {
    const next = id ? await studioApi.run(id).catch(() => null) : null;
    if (!next || !mounted.current) return null;
    setWorkspace(previous => (previous.run?.id === next.id && next.version >= (previous.run.version ?? 0) && !deepEqual(previous.run, next) ? { ...previous, run: next } : previous));
    if (!ACTIVE.includes(next.status)) settleRun(next);
    return next;
  }, [settleRun]);
  // One loop per run while it is queued or running. An answer equal to the run on screen
  // is dropped, so a phase that takes a while renders nothing; a failure backs the next
  // poll off, and after MAX_POLL_FAILURES the error shows and the loop stops.
  const runId = run?.id;
  const polling = !!run && POLLING.includes(run.status);
  useEffect(() => {
    if (!runId || !polling) return undefined;
    let failures = 0;
    let timer = null;
    let stopped = false;
    const tick = async () => {
      try {
        const next = await studioApi.run(runId);
        if (stopped || !mounted.current) return;
        failures = 0;
        setWorkspace(previous => previous.run?.id !== next.id || deepEqual(previous.run, next) ? previous : { ...previous, run: next });
        if (!POLLING.includes(next.status)) {
          // A run that stops on a question has given back what it reserved beyond its use (an
          // art's image question, contract 2.9.0): the balance on screen follows.
          if (!ACTIVE.includes(next.status)) settleRun(next);
          else refreshUsage();
          return;
        }
      } catch (failure) {
        if (stopped || !mounted.current) return;
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) { fail(failure); return; }
      }
      timer = setTimeout(tick, pollMs * (failures + 1));
    };
    timer = setTimeout(tick, pollMs);
    return () => { stopped = true; clearTimeout(timer); };
  }, [runId, polling, pollMs, settleRun, refreshUsage, fail]);

  // The open image question (contract 2.9.0) is signed for `limits.download_ttl_seconds` from
  // the read (its suggestions, current images and draft) and may close on the server (answered
  // elsewhere, expired): the run is read again when its dialog opens, when the tab comes back
  // after a while, and before the URLs expire while it waits.
  const imageAction = run?.status === 'waiting_user' ? run.pending_actions?.find(item => item.status === 'open' && item.kind === 'images') : null;
  const imageStamp = imageAction ? `${run.id}:${imageAction.id}` : null;
  const signedMs = Math.max(RESUME_READ_MS, (capabilities?.limits?.download_ttl_seconds || 900) * 1000 - RESUME_READ_MS);
  const refreshRun = useCallback(() => resyncRun(runId), [resyncRun, runId]);
  const refreshRef = useRef(refreshRun);
  refreshRef.current = refreshRun;
  const readAt = useRef(0);
  useEffect(() => {
    if (!imageStamp) return undefined;
    readAt.current = Date.now();
    const read = () => { readAt.current = Date.now(); refreshRef.current(); };
    const timer = setInterval(read, signedMs);
    const resume = () => { if (document.visibilityState === 'visible' && Date.now() - readAt.current >= RESUME_READ_MS) read(); };
    document.addEventListener('visibilitychange', resume);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', resume); };
  }, [imageStamp, signedMs]);
  const imagesOpen = modal?.type === 'images';
  useEffect(() => {
    if (imagesOpen && imageStamp) { readAt.current = Date.now(); refreshRef.current(); }
  }, [imagesOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── editor: server revision, or the local draft that has not been saved yet ──
  useEffect(() => {
    if (!current) { editorOwner.current = null; keepEditor(null); setDirty(false); return; }
    if (editorOwner.current !== current.id) {
      const draft = owner ? readEditorDraft(owner, current.id) : null;
      setSaveError(null); editorOwner.current = current.id;
      keepEditor(draft || (revision ? { base_revision_id: revision.id, ...revision.script } : null)); setDirty(!!draft);
    } else if (!dirty && revision && editorRef.current?.base_revision_id !== revision.id) {
      keepEditor({ base_revision_id: revision.id, ...revision.script });
    }
  }, [current, revision, dirty, owner, keepEditor]); // preserve edits while a run completes
  const persistEntry = next => {
    setEntry(next);
    try { saveEntry(next); setError(null); } catch { setError('storage'); }
  };
  const updateEntry = patch => {
    const allowed = { ...patch };
    if (formatLocked) { delete allowed.format; delete allowed.formatSelected; }
    persistEntry({ ...entry, ...allowed });
  };
  const goToLogin = () => {
    try { saveEntry({ ...entry, topic, resume: entry.formatSelected === true && (!!entry.prompt.trim() || !!topic) }); auth.goToAuth(); }
    catch { setError('storage'); }
  };
  const onboarding = useOnboarding({ signed: auth.signed, account, setAccount, loadAccount, locale, pollMs, entry, persistEntry,
    goToAuth: auth.goToAuth, act, setError, setModal, mounted, profileAdded });
  const payments = usePayments({ account, features, setModal, refreshUsage, loadAccount, mounted });
  outOfCredits.current = payments.outOfCredits;
  // A plan picked on the landing page before sign-in (`entry.plan`) is offered once the
  // profile is ready to buy it and nothing else is open; a plan already held is dropped.
  useEffect(() => {
    if (!entry.plan || !payments.enabled || onboarding.stage !== 'ready' || modal) return;
    const wanted = entry.plan;
    persistEntry({ ...entry, plan: null });
    if (payments.plan !== wanted && (payments.planInfo(wanted)?.rank ?? 0) > (payments.planInfo(payments.plan)?.rank ?? 0)) payments.openUpgrade(wanted);
  }, [entry.plan, payments.enabled, onboarding.stage, modal]); // eslint-disable-line react-hooks/exhaustive-deps
  // The active profile's personality (contract 2.3.0), which its generations keep.
  const myPersonality = useMyPersonality({ features, profileId, pollMs, setModal, refreshUsage, mounted });
  const automations = useAutomations({ profileId, plan: payments.plan, openUpgrade: payments.openUpgrade });

  // A recorded prompt or reply: its transcript is already in the text, the transcription
  // goes along with it. The home draft keeps it with its owner (the tab can change account).
  const voice = current
    ? (replyVoice?.projectId === current.id ? replyVoice.transcription : null)
    : (owner && entry.voice?.owner === owner ? entry.voice.transcription : null);
  const attachVoice = (transcription, text) => {
    if (current) { setReplyVoice({ projectId: current.id, transcription }); setReply(text); }
    else updateEntry({ prompt: text, voice: { owner, transcription } });
  };
  const discardVoice = () => (current ? setReplyVoice(null) : updateEntry({ voice: null }));
  // The transcript is used only as the text the creator confirmed: the prompt or message sent.
  const reviewVoice = (transcription, text) => transcription.status === 'reviewed' && transcription.text === text ? transcription
    : studioApi.updateTranscription(transcription.id, { intent: 'review', expected_version: transcription.version, text });

  // Generate when the project has no script yet, revise its latest revision otherwise.
  // The project is re-read so the version and base revision are the server's.
  // `extra` carries what only a revision of an art takes: the layers a chat edit targets.
  const startRun = useCallback(async (id, sourceMessageId, extra = {}) => {
    const project = await studioApi.project(id);
    const base = project.latest_revision_id ? { kind: 'revise', base_revision_id: project.latest_revision_id } : { kind: 'generate' };
    const quote = await studioApi.quote(id, { ...base, source_message_id: sourceMessageId });
    const started = await studioApi.startRun(id, { ...base, ...(base.kind === 'revise' ? extra : {}), source_message_id: sourceMessageId, usage_quote_id: quote.id, expected_project_version: project.version });
    setWorkspace(previous => previous.id === id ? { ...previous, project: { ...project, active_run_id: started.id }, run: started } : previous);
    refreshUsage();
    return started;
  }, [refreshUsage]);

  const create = async () => {
    const format = STUDIO_FORMATS.find(item => item.id === entry.format);
    if (submitLock.current || auth.loading || entry.formatSelected !== true || !format || !(entry.prompt.trim() || topic)) return;
    // An art format waits for the capabilities that offer it (a visitor's draft resumes after sign-in).
    if (auth.signed && account.status === 'ready' && !formatAvailable(format, capabilities)) return;
    if (!auth.signed && !format.available && !format.art) return;
    setHasPrompted(true);
    if (!auth.signed) { goToLogin(); return; }
    if (account.status === 'error') { loadAccount(); return; }
    if (account.status !== 'ready') return;
    // The images go only once every one is uploaded (the send button waits for them too).
    if (format.art && attachments.busy) return;
    // Until the onboarding connects a profile the home shows it; the prompt comes after.
    if (!account.creator || account.creator.onboarding_status === 'pending' || !profileId) return;
    submitLock.current = true;
    setError(null);
    try {
      // Only a topic still in the live list is sent, with its current evidence.
      const chosen = topic && account.topics.find(item => item.id === topic.id);
      // The chosen topic sits in the composer as a chip; the brief carries its id and
      // evidence, not its words, so the prompt is the topic's title, then what was typed.
      const typed = entry.prompt.trim();
      const prompt = [...new Set([topic?.title, typed].filter(Boolean))].join('\n\n');
      // Unused prompt audio is deleted after 24 h; its transcript is still the typed text.
      const reviewed = voice && typed ? await reviewVoice(voice, typed).catch(failure => { if (failure.status === 404) return null; throw failure; }) : null;
      if (reviewed !== voice) updateEntry({ voice: reviewed ? { owner, transcription: reviewed } : null });
      // An art brief (2.7.0 proposal) carries the ratio and the template chosen in the gallery.
      const artBrief = format.art ? { art: { aspect_ratio: entry.art?.aspect_ratio || ART_DEFAULT_RATIO, template_id: entry.art?.template?.id || null } } : {};
      // The attached images ride on the prompt, which is the chat's first message.
      const attached = format.art ? attachments.payload('home') : [];
      const shown = format.art ? attachments.views('home') : [];
      const project = await studioApi.createProject({ prompt, profile_id: profileId, brief: { format: entry.format,
        topic: chosen ? { kind: 'sourced', topic_id: chosen.id, evidence_ids: chosen.evidence.map(e => e.id) } : null,
        instructions: null, ...artBrief }, ...(reviewed ? { transcription_id: reviewed.id } : {}), ...(attached.length ? { attachments: attached } : {}) }, creationKey.current);
      clearEntry(); setEntry(emptyEntry()); setTopic(null); clearAttachments('home'); creationKey.current = idempotencyKey('project');
      setAccount(a => ({ ...a, projects: upsert(a.projects, project) }));
      // The prompt is the chat's first message: show it while the run is quoted and
      // started; the chat read after it replaces it with the server's (same id).
      const first = project.initial_message_id ? [{ id: project.initial_message_id, chat_id: project.chat_id, project_id: project.id,
        role: 'user', text: prompt, purpose: 'instruction', transcription_id: reviewed?.id || null, run_id: null, timestamps: project.timestamps, ...(shown.length ? { attachments: shown } : {}) }] : [];
      setWorkspace({ ...emptyWorkspace(project.id, 'ready'), project, messages: first });
      navigate(`/studio/projects/${project.id}`);
      try { await startRun(project.id, project.initial_message_id); }
      catch (failure) { fail(failure); }
      await loadProject(project.id, { quiet: true });
    } catch (failure) { fail(failure); }
    finally { submitLock.current = false; }
  };
  // Resume a public draft only after an actual authenticated creator exists.
  useEffect(() => {
    if (account.creator && auth.signed && readEntry().resume && !projectId) create();
  }, [account.creator?.id, auth.signed]); // eslint-disable-line react-hooks/exhaustive-deps

  // A profile's workspace settings (contract 2.0.0): interests, audience, voice, memory choice.
  const saveProfile = (target, values) => act(async () => {
    const saved = await studioApi.updateProfile(target.id, { expected_version: target.version, ...values });
    setAccount(a => ({ ...a, creator: a.creator && { ...a.creator, profiles: (a.creator.profiles || []).map(item => item.id === saved.id ? saved : item) } }));
    return saved;
  });
  // Another profile is another workspace: what was drafted, chosen or open in the previous
  // one stays there (an open project of the other profile goes back home).
  const previousProfile = useRef(profileId);
  useEffect(() => {
    const previous = previousProfile.current;
    previousProfile.current = profileId;
    if (!previous || !profileId || previous === profileId) return;
    setTopic(null); setDismissed([]); setReply(''); setReplyVoice(null); setHasPrompted(false); setError(null); resetAttachments();
    setBriefTopics({}); briefTopicRequests.current.clear();
    creationKey.current = idempotencyKey('project');
    persistEntry({ ...emptyEntry(), format: entry.format });
    if (projectId && workspace.project?.id === projectId && workspace.project.profile_id !== profileId) navigate(STUDIO_HOME);
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps
  // A chosen topic joins the prompt as a chip (ComposerInput's TopicToken), never as its
  // text: what was typed stays, and the title is added when the project is created.
  const chooseTopic = chosen => { setTopic(chosen); updateEntry({ topic: chosen }); setModal(null); };
  // From the trending gallery: a fresh creation on the home, in the chosen format, with
  // the topic in the prompt as its chip, ready to direct and send.
  const startFromTopic = (chosen, format) => {
    setTopic(chosen); setHasPrompted(false); setError(null); setModal(null);
    persistEntry({ ...entry, prompt: '', topic: chosen, format, formatSelected: true, resume: false });
    navigate(STUDIO_HOME);
  };
  // The choice is kept with the draft, so it can outlive its snapshot or come from an
  // older build; once the live list is known, keep it only while it is still offered.
  useEffect(() => {
    if (account.status === 'ready' && account.scopeReady && topic && !account.topics.some(item => item.id === topic.id)) { setTopic(null); updateEntry({ topic: null }); }
  }, [account.status, account.scopeReady, account.topics]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── script autosave ──
  const currentId = current?.id;
  const currentVersion = current?.version;
  const saveScript = useCallback(async () => {
    if (!owner || !currentId || !editor || editorOwner.current !== currentId) return false;
    if (!dirty) return revision || false;
    if (savingRef.current) return savingRef.current;
    const sent = editor;
    if (!sent.blocks.length || sent.blocks.some(block => !block.spoken_text.trim())) { setSaveError('emptyScript'); setError('emptyScript'); return false; }
    setSaving(true);
    savingRef.current = (async () => {
      try {
        const result = await studioApi.saveRevision(currentId, { base_revision_id: sent.base_revision_id, expected_project_version: currentVersion, title: sent.title,
          blocks: sent.blocks.map(({ id, kind, spoken_text, recording_notes }) => ({ id: id || null, kind, spoken_text, recording_notes: recording_notes || '' })),
          evidence_ids: sent.evidence_ids || [] });
        const project = await studioApi.project(currentId).catch(() => null);
        if (editorRef.current === sent) {
          keepEditor({ base_revision_id: result.id, ...result.script }); setDirty(false); clearEditorDraft(owner, currentId);
        } else {
          // The creator kept typing during the request: keep that text, now based on the new revision.
          const next = { ...editorRef.current, base_revision_id: result.id };
          keepEditor(next);
          try { saveEditorDraft(owner, currentId, next); } catch { /* The text stays on screen. */ }
        }
        setWorkspace(previous => previous.id === currentId ? { ...previous,
          project: project || { ...previous.project, latest_revision_id: result.id, version: previous.project.version + 1 },
          revisions: [...previous.revisions.filter(r => r.id !== result.id), result] } : previous);
        setSaveError(null); setError(null);
        return result;
      } catch (failure) {
        const key = errorKey(failure);
        setSaveError(key); setError(key);
        // Load the newer revision so the editor can offer to keep or discard the draft.
        if (key === 'conflict') loadProject(currentId, { quiet: true });
        return false;
      } finally { savingRef.current = null; if (mounted.current) setSaving(false); }
    })();
    return savingRef.current;
  }, [owner, currentId, currentVersion, editor, dirty, revision, loadProject, keepEditor]);
  useEffect(() => {
    if (!dirty || !editor || !currentId || saveError) return;
    const timer = setTimeout(saveScript, 3000);
    return () => clearTimeout(timer);
  }, [dirty, editor, currentId, saveError, saveScript]);
  const editScript = (blockId, field, value) => {
    const next = { ...editor, blocks: editor.blocks.map(b => b.id === blockId ? { ...b, [field]: value } : b) };
    keepEditor(next); setDirty(true); setSaveError(null);
    try { saveEditorDraft(owner, current.id, next); } catch { setError('storage'); }
  };
  const restoreSavedScript = () => {
    clearEditorDraft(owner, current.id);
    keepEditor({ base_revision_id: revision.id, ...revision.script }); setDirty(false); setSaveError(null); setError(null);
  };
  const rebaseScript = () => {
    const next = { ...editor, base_revision_id: revision.id };
    keepEditor(next); setSaveError(null); setError(null);
    try { saveEditorDraft(owner, current.id, next); } catch { setError('storage'); }
  };
  // Copy, export and teleprompter use the saved revision; pending edits are saved first.
  const savedRevision = async () => (dirty ? (await saveScript()) || null : revision || null);

  // ── chat and runs ──
  const send = async () => {
    const text = replyDraft.get().trim();
    if (!text || active || submitLock.current || !current || attachments.busy) return;
    submitLock.current = true;
    setError(null);
    const artProject = isArtProject(current);
    const attached = artProject ? attachments.payload(current.id) : [];
    try {
      // The AI revises what is saved: pending edits (script or art) are saved first.
      if (artProject ? art.dirty && !(await art.save()) : dirty && !(await saveScript())) return;
      const reviewed = voice ? await reviewVoice(voice, text) : null;
      if (reviewed) setReplyVoice({ projectId: current.id, transcription: reviewed });
      const message = await studioApi.createMessage(current.chat_id, { text, purpose: current.latest_revision_id ? 'revision' : 'instruction',
        ...(reviewed ? { transcription_id: reviewed.id } : {}), ...(attached.length ? { attachments: attached } : {}) });
      setReply(''); setReplyVoice(null); clearAttachments(current.id);
      setWorkspace(previous => previous.id === current.id ? { ...previous, messages: [...previous.messages, message] } : previous);
      const options = artProject ? art.runOptions() : {};
      if (artProject) art.clearScope();
      await startRun(current.id, message.id, options);
    } catch (failure) { fail(failure); }
    finally { submitLock.current = false; }
  };
  const answer = async (kind, text = null, memoryId = null, transcriptionId = null) => {
    const action = run?.pending_actions?.find(item => item.status === 'open');
    if (!action) throw Object.assign(new Error('No open action'), { code: 'action_not_open' });
    const body = { kind, expected_action_version: action.version };
    if (kind === 'reuse') {
      body.memory_id = memoryId;
      body.expected_memory_version = account.memories.find(memory => memory.id === memoryId)?.version;
    }
    if (kind === 'answer') body.text = text;
    if (kind === 'submit_story') {
      const message = await studioApi.createMessage(current.chat_id, { text, purpose: 'story', ...(transcriptionId ? { transcription_id: transcriptionId } : {}) });
      setWorkspace(previous => previous.id === current.id ? { ...previous, messages: [...previous.messages, message] } : previous);
      body.message_id = message.id;
    }
    const response = await studioApi.respond(run.id, action.id, body).catch(async failure => {
      if (STALE_RUN.includes(failure?.code) || transient(failure)) await resyncRun(run.id);
      throw failure;
    });
    setWorkspace(previous => previous.run?.id === response.run.id ? { ...previous, run: response.run } : previous);
    if (kind === 'submit_story') refreshMemories();
    return response;
  };
  const respond = (kind, text, memoryId, transcriptionId) => act(() => answer(kind, text, memoryId, transcriptionId));
  // ── the image question of an art run (contract 2.9.0) ──
  // The open question is `s.run.pending_actions.find(a => a.status === 'open' && a.kind === 'images')`;
  // what it offers follows the capabilities' features on `s.features`: `art_image_generation`
  // ("Gerar com IA", each place's `generation_credits`) and `community_images` ("Da comunidade").
  // Each call returns the response, or rejects with the StudioError: `insufficient_credits` has
  // already opened the plans (`payments.outOfCredits`, the balance read again); a `usage_quote_*`
  // code means quote again. A question the server closed meanwhile (409 `action_not_open` or
  // `action_version_conflict`, or a lost response) reads the run again, which then polls or
  // settles and closes the dialog; when the question is gone the call rejects with
  // `images_question_closed`, shown by the studio (`s.error`), as is an answer's failure once
  // its dialog was closed (the dialog shows its own while open).
  const questionClosed = failure => Object.assign(new Error('The image question closed'), { studio: true, code: 'images_question_closed', status: failure?.status ?? 409,
    details: failure?.details || {}, errors: {}, retryable: false });
  const imageCall = async (action, fn, answering = false) => {
    try { return await fn(); }
    catch (failure) {
      if (errorKey(failure) === 'credits') { outOfCredits.current?.(); refreshUsage(); throw failure; }
      let error = failure;
      if (STALE_RUN.includes(failure?.code) || transient(failure)) {
        const next = await resyncRun(action.run_id);
        if (next && !next.pending_actions?.some(item => item.id === action.id && item.status === 'open')) error = questionClosed(failure);
      }
      // A quote asked again needs the dialog: without it the answer simply failed.
      if (error !== failure || (answering && modalRef.current?.type !== 'images')) setError(/^usage_quote_/.test(error.code || '') ? 'generic' : errorKey(error));
      throw error;
    }
  };
  // `{ kind: image }` quote of the places to generate (`slotIds`, each place's `slot_id`).
  const imageQuote = (action, slotIds) => imageCall(action, () => studioApi.quote(run?.project_id || current.id, { kind: 'image', action_id: action.id, slot_ids: slotIds }));
  // Answering moves the run on (its images phase): the run on screen is the one answered. The
  // key is the answer's own, so a response lost on the way is asked for once more and core
  // replays it when it had committed the answer.
  const answerImages = (action, body) => imageCall(action, async () => {
    const sent = { ...body, expected_action_version: action.version };
    const key = requestKey(`images-${action.id}-${action.version}`, sent);
    const response = await studioApi.respond(action.run_id, action.id, sent, key)
      .catch(failure => (transient(failure) ? studioApi.respond(action.run_id, action.id, sent, key) : Promise.reject(failure)));
    setWorkspace(previous => (previous.run?.id === response.run.id && response.run.version >= (previous.run.version ?? 0) ? { ...previous, run: response.run } : previous));
    refreshUsage();
    return response;
  }, true);
  // One choice per place, `{ slot_id, source: generate|asset|community|keep, asset_id?, community_image_id?, subject? }`;
  // only what the source takes is sent (a `generate` subject only when the creator wrote one).
  const imageChoice = ({ slot_id: slotId, source, asset_id: assetId, community_image_id: communityId, subject }) => ({ slot_id: slotId, source,
    ...(source === 'asset' ? { asset_id: assetId } : {}), ...(source === 'community' ? { community_image_id: communityId } : {}),
    ...(source === 'generate' && subject?.trim() ? { subject: subject.trim() } : {}) });
  const chooseImages = (action, choices, quoteId = null) => answerImages(action, { kind: 'choose_images', images: choices.map(imageChoice), ...(quoteId ? { usage_quote_id: quoteId } : {}) });
  // Keeps every current image.
  const skipImages = action => answerImages(action, { kind: 'skip' });
  const cancelRun = () => act(async () => {
    const next = await studioApi.updateRun(run.id, { intent: 'cancel', expected_version: run.version }).catch(async failure => {
      if (STALE_RUN.includes(failure?.code) || transient(failure)) await resyncRun(run.id);
      throw failure;
    });
    setWorkspace(previous => previous.run?.id === next.id ? { ...previous, run: next } : previous);
    settleRun(next);
    return next;
  });
  // A recoverable failure resumes the same run and operation; a cancelled or
  // unrecoverable run starts over from the same instruction with a new quote.
  const retryRun = () => act(async () => {
    if (run.status === 'failed') {
      try {
        const next = await studioApi.updateRun(run.id, { intent: 'retry', expected_version: run.version });
        setWorkspace(previous => previous.run?.id === next.id ? { ...previous, run: next } : previous);
        refreshUsage();
        return next;
      } catch (failure) { if (failure.code !== 'invalid_run_transition') throw failure; }
    }
    return startRun(run.project_id, run.source_message_id);
  });

  // ── stories, audio and memory ──
  const storySubmit = (text, remember, transcription) => act(async () => {
    text = text.trim();
    const transcriptionId = transcription ? (await reviewVoice(transcription, text)).id : null;
    // Saved explicitly before the story message, so it is stored as confirmed, not proposed.
    if (remember) {
      const memory = await studioApi.createMemory({ profile_id: profileId, text, sensitive: false });
      setAccount(a => ({ ...a, memories: upsert(a.memories, memory) }));
    }
    if (run?.status === 'waiting_user') await answer('submit_story', text, null, transcriptionId);
    else if (current) setReply(text);
    else updateEntry({ prompt: `${entry.prompt}${entry.prompt ? '\n\n' : ''}${text}` });
    setModal(null);
    return true;
  });
  // Recorded audio → private asset → quoted transcription, polled until ready. Inside a
  // project the audio is the project's; on the home composer (contract 1.5.0) it waits in
  // the active profile's workspace (2.0.0) until a project of that profile adopts it. The
  // transcript is a draft the creator reviews before it is used.
  const transcribe = (blob, mimeType) => act(async () => {
    const projectId = current?.id;
    const asset = await uploadAsset({ purpose: 'audio', ...(projectId ? { project_id: projectId } : { profile_id: profileId }), mime_type: mimeType }, blob);
    const quote = projectId ? await studioApi.quote(projectId, { kind: 'transcribe', asset_id: asset.id })
      : await studioApi.audioQuote({ kind: 'transcribe', asset_id: asset.id });
    const start = { asset_id: asset.id, usage_quote_id: quote.id, locale };
    let transcription = projectId ? await studioApi.startTranscription(projectId, start) : await studioApi.startAudioTranscription(start);
    while (['queued', 'running'].includes(transcription.status) && mounted.current) {
      await wait(pollMs);
      transcription = await studioApi.transcription(transcription.id);
    }
    refreshUsage();
    if (transcription.status === 'failed') throw Object.assign(new Error('Transcription failed'), { code: transcription.error?.code === 'insufficient_credits' ? 'insufficient_credits' : 'transcription_failed' });
    return transcription;
  });
  const saveMemory = (text, memoryId) => act(async () => {
    const existing = memoryId && account.memories.find(memory => memory.id === memoryId);
    const memory = existing
      ? await studioApi.updateMemory(existing.id, { expected_version: existing.version, text })
      : await studioApi.createMemory({ profile_id: profileId, text, sensitive: false });
    setAccount(a => ({ ...a, memories: upsert(a.memories, memory) }));
    return memory;
  });
  const confirmMemory = existing => act(async () => {
    const memory = await studioApi.updateMemory(existing.id, { expected_version: existing.version, status: 'confirmed' });
    setAccount(a => ({ ...a, memories: upsert(a.memories, memory) }));
    return memory;
  });
  const deleteMemory = existing => act(async () => {
    await studioApi.deleteMemory(existing.id);
    setAccount(a => ({ ...a, memories: a.memories.filter(memory => memory.id !== existing.id) }));
  });

  // ── projects, topics, exports ──
  const archive = id => act(async () => {
    const project = await studioApi.project(id);
    await studioApi.updateProject(id, { expected_version: project.version, status: 'archived' });
    setAccount(a => ({ ...a, projects: a.projects.filter(p => p.id !== id) }));
  });
  const dismissTopic = id => setDismissed(list => [...list, id]);
  const refreshTopics = () => act(async () => {
    setTopicsState('loading');
    try {
      const id = profileId;
      const page = features.trending_topics && id ? await studioApi.topics({ ...TOPICS, profile_id: id }) : { data: [] };
      if (inScope(id)) { setAccount(a => ({ ...a, topics: page.data })); setDismissed([]); }
    } finally { if (mounted.current) setTopicsState('ready'); }
  });
  // An older revision on screen is exported as it is; otherwise the latest, saved first.
  const exportScript = (format, chosen = null) => act(async () => {
    const saved = chosen || await savedRevision();
    if (!saved) return false;
    return exportReel({ projectId: current.id, revision: saved, format, t, persist: features.exports !== false });
  });

  // `scope` is the profile the lists belong to, `scopeReady` once they are loaded for it.
  // Its identity, like its lists', moves only when one of them does (memoStudio compares
  // `snapshot.messages`, `snapshot.revisions`…), never with the run polling.
  const messages = current ? workspace.messages : NONE;
  const revisions = current ? workspace.revisions : NONE;
  const artRevisions = current ? workspace.artRevisions : NONE;
  const snapshot = useMemo(() => ({
    creator: account.creator, projects: account.projects, memories: account.memories, usage: account.usage, scope: account.scope, scopeReady: account.scopeReady,
    messages, revisions, artRevisions,
  }), [account.creator, account.projects, account.memories, account.usage, account.scope, account.scopeReady, messages, revisions, artRevisions]);
  const topics = useMemo(() => account.topics.filter(item => !dismissed.includes(item.id)), [account.topics, dismissed]);
  // The actions keep one identity each, so the screens can memoize on them (memoStudio).
  const actions = useStableActions({
    updateEntry, refreshTopics, clearTopic: () => { setTopic(null); updateEntry({ topic: null }); }, dismissTopic,
    act, create, goToLogin, saveProfile, chooseTopic, startFromTopic, send, editScript, saveScript, restoreSavedScript, rebaseScript, savedRevision,
    respond, cancelRun, retryRun, storySubmit, transcribe, attachVoice, discardVoice, saveMemory, confirmMemory, deleteMemory, archive, exportScript,
    imageQuote, chooseImages, skipImages, refreshRun,
    // The home composer's art choices (kept with the draft entry): the template picked in the
    // gallery (its document rides along for the chip's thumbnail) and the aspect ratio.
    chooseArtTemplate: template => { updateEntry({ art: { ...(entry.art || {}), template: { id: template.id, name: template.name, document: template.document } } }); setModal(null); },
    clearArtTemplate: () => updateEntry({ art: { ...(entry.art || {}), template: null } }),
    setArtRatio: ratio => updateEntry({ art: { ...(entry.art || {}), aspect_ratio: ratio } }),
    newChat: () => { onboarding.stopAdding(); navigate('/studio'); setReply(''); setError(null); setHasPrompted(false); persistEntry({ ...entry, formatSelected: false, resume: false }); },
  });
  // Before sign-in the home is the landing page, scrolling inside the shell.
  const landing = ['/studio', '/studio/'].includes(location.pathname) && !auth.signed && !auth.loading;
  useStudioTracking({ location, landing });
  return { t, locale, auth, navigate, location, snapshot, capabilities, features, current, run, revision, active, onboarding, payments, myPersonality, automations, accounts, profile, projectTopic, art,
    accountStatus: account.status,
    projectStatus: !projectId ? null : workspace.id === projectId ? workspace.status
      : ['ready', 'unavailable', 'error'].includes(account.status) && !account.creator ? 'missing' : 'loading',
    credits: account.usage?.available_units ?? null,
    resting: ['/studio', '/studio/'].includes(location.pathname) && !hasPrompted && !auth.loading,
    landing,
    attachments,
    entry, formatLocked, replyDraft, setReply, editor, dirty, saving, saveError, error, setError, notice, setNotice,
    modal, setModal, topicsState, topic, topics, voice,
    ...actions,
  };
}
